import { AfterViewInit, Component, Input, OnChanges } from '@angular/core';
import { FormArray, FormControl, FormGroup, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngneat/elf';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { FrictionalPressureLossProfile } from 'src/app/perical/models/fluid.model';
import { GetFeetCubicPerSecondFromValue, GetPpgFromValue, GetPsiPer100ftFromValue } from 'src/app/perivis/shared/helpers/units.helper';
import { zipObject } from 'lodash-es';
import {
  createDefaultPolymerFrictionLossRow,
  createPolymerFrictionLossColumns,
  createPolymerFrictionLossRow,
  ensureTrailingInputRow,
  extractPolymerFrictionLossRows,
  populatePolymerFrictionLossFormArray
} from './polymers-grid.helper';

export interface PolymersForm {
  name: string;
  type: string;
  nPrime: number;
  kPrime: number;
  rheologyTemperature: number;
  frictionalPressureLossProfiles: FrictionalPressureLossProfile[];
  modelFrictionReduction: boolean;
  density: number;
}

@Component({
  selector: 'app-polymers',
  templateUrl: './polymers.component.html',
  styles: [],
  providers: subformComponentProviders(PolymersComponent),
  standalone: false
})
export class PolymersComponent implements OnChanges, AfterViewInit {
  public control(name: string): UntypedFormControl {
    return this.form.formGroup.get(name) as UntypedFormControl;
  }

  @Input()
  public userUnits: UserUnitsModel;
  
  @Input()
  public isReadOnly: boolean;

  @Input()
  public isInUse: boolean;

  @Input()
  public fluidsStore: Store;

  @Input()
  public fuidChangeTrigger: any | null | undefined;

  public showPlot = false;
  public axisType = 'linear';
  public xAxisTitle: string;
  public yAxisTitle: string;
  public plotName = 'polymer_pressure_loss';
  public disableFrictionReduction: boolean;
  public densityUnits: string;
  public diameterUnits: string;
  public flowRateUnits: string;
  public interpolatedPoint: number | null = null;
  public pressureLossUnits: string;
  public interpForm: UntypedFormGroup;
  public frictionalPressureLossRows = new FormArray<FormGroup>([]);
  public readonly frictionalPressureLossColumns = createPolymerFrictionLossColumns();
  public readonly createFrictionalPressureLossRow = createPolymerFrictionLossRow;
  public plotFrictionFactor = false;
  public frictionalPressureProfilesFriction: FrictionalPressureLossProfile[] = [];
  public temperatureValidation: { min: number, max: number };
  public showFrictionFactorTable: boolean;
  public resultsGrid: any[] = [];
  public cols: any[];
  public tempUnit: string;
  public kPrimeUnit: string;

  public plot = {
    data: [],
    layout: {},
    config: {}
  };

  constructor(private formBuilder: UntypedFormBuilder) {
    this.interpForm = this.formBuilder.group({
      innerDiameterInput: new FormControl(null),
      flowRateInput: new FormControl(null)
    });
  }

  public form = createForm<PolymersForm>(this, {
    formType: FormType.SUB,
    formControls: {
      name: new UntypedFormControl(null),
      type: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      nPrime: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      kPrime: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      rheologyTemperature: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      frictionalPressureLossProfiles: new FormControl([]),
      modelFrictionReduction: new UntypedFormControl(false),
      density: new UntypedFormControl(null)
    },
    fromFormGroup: (formGroup: PolymersForm) => {
      if (!formGroup) {
        return null;
      }

      return {
        name: formGroup.name,
        type: formGroup.type,
        nPrime: formGroup.nPrime,
        kPrime: formGroup.kPrime,
        rheologyTemperature: formGroup.rheologyTemperature,
        frictionalPressureLossProfiles: formGroup.frictionalPressureLossProfiles.sort((a, b) => a.tubularInnerDiameter - b.tubularInnerDiameter),
        modelFrictionReduction: formGroup.modelFrictionReduction,
        density: formGroup.density
      };
    }
  });

  ngOnChanges(): void {
    if (!this.userUnits) {
      return;
    }
    this.densityUnits = unitsLib[this.userUnits.density].symbol;
    this.diameterUnits = this.userUnits.shortLengths;
    this.flowRateUnits = unitsLib[this.userUnits.standardFluidFlow].symbol;
    this.pressureLossUnits = this.userUnits.pressureLoss;
    this.tempUnit = this.userUnits.temperature;
    this.kPrimeUnit = this.userUnits.kPrime;

    switch (this.tempUnit) {
      case '°F':
        this.temperatureValidation = { min: 0, max: 1000 };
        break;
      case '°C':
        this.temperatureValidation = { min: -17.7, max: 537 };
        break;
      case 'K':
        this.temperatureValidation = { min: 255, max: 810 };
        break;
    }

    this.form.formGroup.controls.rheologyTemperature.setValidators([Validators.required, Validators.min(this.temperatureValidation.min), Validators.max(this.temperatureValidation.max)]);

    this.cols = [
      { field: 'tubularInnerDiameter', header: `Pipe ID (${this.userUnits.shortLengths})`, valueFormatter: (params: any) => this.formatDecimal(params, 4) },
      { field: 'flowRateOne', header: `Q1 (${this.flowRateUnits})`, valueFormatter: (params: any) => this.formatDecimal(params, 4) },
      { field: 'pressureLossOne', header: `FF1`, valueFormatter: (params: any) => this.formatDecimal(params, 4) },
      { field: 'flowRateTwo', header: `Q2 (${this.flowRateUnits})`, valueFormatter: (params: any) => this.formatDecimal(params, 4) },
      { field: 'pressureLossTwo', header: `FF2`, valueFormatter: (params: any) => this.formatDecimal(params, 4) },
      { field: 'flowRateThree', header: `Q3 (${this.flowRateUnits})`, valueFormatter: (params: any) => this.formatDecimal(params, 4) },
      { field: 'pressureLossThree', header: `FF3`, valueFormatter: (params: any) => this.formatDecimal(params, 4) },
    ];

    this.modelFrictionReductionChange();
  }

  ngAfterViewInit(): void {
    this.modelFrictionReductionChange();
    let profiles = this.form.formGroup.controls.frictionalPressureLossProfiles.value ?? [];
    if (profiles.length === 0) {
      profiles = [createDefaultPolymerFrictionLossRow(this.userUnits)];
      this.form.formGroup.controls.frictionalPressureLossProfiles.setValue(profiles);
    }

    populatePolymerFrictionLossFormArray(this.frictionalPressureLossRows, profiles);
  }

  public modelFrictionReductionChange() {
    this.disableFrictionReduction = this.form.formGroup.controls.modelFrictionReduction.value;
  }

  public onFrictionalPressureLossCellEdited(): void {
    ensureTrailingInputRow(this.frictionalPressureLossRows);
  }

  public onFrictionalPressureLossRowDeleted(index: number): void {
    if (index >= 0 && index < this.frictionalPressureLossRows.length) {
      this.frictionalPressureLossRows.removeAt(index);
    }
    this.onFrictionalPressureLossGridCommit();
  }

  public onFrictionalPressureLossRowsReset(): void {
    populatePolymerFrictionLossFormArray(this.frictionalPressureLossRows, []);
    this.syncFrictionalPressureLossProfiles();
  }

  public onFrictionalPressureLossGridCommit(): void {
    ensureTrailingInputRow(this.frictionalPressureLossRows);
    this.syncFrictionalPressureLossProfiles();
  }

  private syncFrictionalPressureLossProfiles(): void {
    this.form.formGroup.controls.frictionalPressureLossProfiles.setValue(
      extractPolymerFrictionLossRows(this.frictionalPressureLossRows)
    );
  }

  tablePlotToggle() {
    this.showPlot = !(this.showPlot ?? false);
    if (this.showPlot) {
      this.plotFrictionFactor = false;
      this.showFrictionFactorTable = false;
      this.plotData();
    } else {
      this.plotName = 'polymer_pressure_loss';
      this.axisType = 'linear';
      this.plot.data = [];
      this.plotFrictionFactor = false;
      this.showFrictionFactorTable = false;
    }
    
    if (this.interpForm.controls.innerDiameterInput.value && this.interpForm.controls.flowRateInput.value) {
      this.interpolatePressureLoss();
    }
  }

 private plotData(): void {
  this.xAxisTitle = `Flow Rate Q (${this.flowRateUnits})`;
  this.yAxisTitle = this.plotFrictionFactor ? 'Fanning Friction Factor' : `Pressure Loss (${this.pressureLossUnits})`
  this.plot.data = [];

  this.plot.layout = {
    ...this.plot.layout,
    xaxis: {
      title: this.xAxisTitle,
      type: this.axisType,
      autorange: true
    },
    yaxis: {
      title: this.yAxisTitle,
      type: this.axisType,
      autorange: true
    }
  };

  if (this.form.formGroup.controls.frictionalPressureLossProfiles.value.length > 0) {
    if (!this.plotFrictionFactor) {
      const plotData = [];
      this.form.formGroup.controls.frictionalPressureLossProfiles.value.forEach(profile => {
        const trace = {
          x: [profile.flowRateOne, profile.flowRateTwo, profile.flowRateThree],
          y: [profile.pressureLossOne, profile.pressureLossTwo, profile.pressureLossThree],
          type: this.axisType,
          mode: 'lines+markers',
          name: `Pipe ID ${profile.tubularInnerDiameter} ${this.userUnits.shortLengths}`
        };
        plotData.push(trace);
      });
      this.plot.data = plotData;
    } else {
      this.calculateFrictionFactorPlotData();
    }

    if (this.interpolatedPoint !== null) {
      const interpolatedTrace = {
        x: [this.interpForm.controls.flowRateInput.value],
        y: [this.interpolatedPoint],
        type: this.axisType,
        mode: 'markers',
        name: `Interpolated Point`,
        marker: { color: 'red', size: 10 },
      };
      this.plot.data.push(interpolatedTrace);
    }
  }
}

private calculateFrictionFactorPlotData() {
  this.frictionalPressureProfilesFriction = [];
  const plotData = [];
  this.form.formGroup.controls.frictionalPressureLossProfiles.value.forEach(profile => {
    const flowRatesFeetPerSecond = [
      GetFeetCubicPerSecondFromValue(profile.flowRateOne, this.flowRateUnits),
      GetFeetCubicPerSecondFromValue(profile.flowRateTwo, this.flowRateUnits),
      GetFeetCubicPerSecondFromValue(profile.flowRateThree, this.flowRateUnits)];
    const pressureLosses = [
      GetPsiPer100ftFromValue(profile.pressureLossOne, this.pressureLossUnits), 
      GetPsiPer100ftFromValue(profile.pressureLossTwo, this.pressureLossUnits), 
      GetPsiPer100ftFromValue(profile.pressureLossThree, this.pressureLossUnits)];

    const crossectionalAreas = [
      Math.PI * Math.pow(profile.tubularInnerDiameter, 2) / 144 / 4,
      Math.PI * Math.pow(profile.tubularInnerDiameter, 2) / 144 / 4,
      Math.PI * Math.pow(profile.tubularInnerDiameter, 2) / 144 / 4
    ]
    const density = GetPpgFromValue(this.form.formGroup.controls.density.value, this.densityUnits);

    const flowRates = [];
    const yValues = [];

    let pressureLossOne = 0;
    let pressureLossTwo = 0;
    let pressureLossThree = 0;

    for (let i = 0; i < 3; i++) {
      const velocity = flowRatesFeetPerSecond[i] / crossectionalAreas[i];
      const frictionFactor = 25.80627 * profile.tubularInnerDiameter * pressureLosses[i] / 100 / density / velocity / velocity;

      yValues.push(frictionFactor);

      if (i === 0) {
        pressureLossOne = frictionFactor;
        flowRates.push(profile.flowRateOne);
      } else if (i === 1) {
        pressureLossTwo = frictionFactor;
        flowRates.push(profile.flowRateTwo);
      } else if (i === 2) {
        pressureLossThree = frictionFactor;
        flowRates.push(profile.flowRateThree);
      }
    }

    const frictionFactorRow: FrictionalPressureLossProfile = {
      tubularInnerDiameter: profile.tubularInnerDiameter,
      flowRateOne: profile.flowRateOne,
      pressureLossOne: pressureLossOne,
      flowRateTwo: profile.flowRateTwo,
      pressureLossTwo: pressureLossTwo,
      flowRateThree: profile.flowRateThree,
      pressureLossThree: pressureLossThree
    };

    this.frictionalPressureProfilesFriction.push(frictionFactorRow);

    if (flowRates.length > 0) {
      const trace = {
        x: flowRates,
        y: yValues,
        type: this.axisType,
        mode: 'lines+markers',
        name: `Pipe ID ${profile.tubularInnerDiameter} ${this.userUnits.shortLengths}`
      };
      plotData.push(trace);
    }
  });
  this.plot.data = plotData;
}

onPlotFrictionFactorChange() {
  this.fluidsStore.update(state => ({
    ...state,
    UIIds: [],
    UIEntities: []
  }));
  this.plotFrictionFactor = !this.plotFrictionFactor;
  this.showFrictionFactorTable = this.plotFrictionFactor;
  this.updatePlotName();
  this.plotData();

  if (this.interpForm.controls.innerDiameterInput.value && this.interpForm.controls.flowRateInput.value) {
    this.interpolatePressureLoss();
  }
}

public onFrictionFactorTableChange() {
  this.calculateFrictionFactorPlotData();
  this.showFrictionFactorTable = !this.showFrictionFactorTable;
  this.fluidsStore.update(state => ({
    ...state,
    UIIds: [],
    UIEntities: []
  }));
  let results = []
  if (this.showFrictionFactorTable) {
    results = this.frictionalPressureProfilesFriction.map(item => [
      item.tubularInnerDiameter,
      item.flowRateOne,
      item.pressureLossOne,
      item.flowRateTwo,
      item.pressureLossTwo,
      item.flowRateThree,
      item.pressureLossThree
    ]);

    this.resultsGrid = results.map(r => zipObject(this.cols.map(c => c.field), r));
  }

  if (this.interpForm.controls.innerDiameterInput.value && this.interpForm.controls.flowRateInput.value) {
    this.interpolatePressureLoss();
  }
}

public onLogChange() {
  this.fluidsStore.update(state => ({
    ...state,
    UIIds: [],
    UIEntities: []
  }));
  this.axisType = this.axisType === 'linear' ? 'log' : 'linear';
  this.updatePlotName();
  this.plotData();
  if (this.interpForm.controls.innerDiameterInput.value && this.interpForm.controls.flowRateInput.value) {
    this.interpolatePressureLoss();
  }
}

private formatDecimal(value: any, numDecimalPoints: number): string {
  return !isNaN(value) ? parseFloat(value).toFixed(numDecimalPoints) : value;
}

private updatePlotName() {
  this.plotName = this.plotFrictionFactor ? 'polymer_friction_factor' : 'polymer_pressure_loss';
  if (this.axisType === 'log') {
    this.plotName += '_log';
  } else {
    this.plotName += '_linear';
  }
}

 public interpolatePressureLoss() {
  if (!this.interpForm.controls.innerDiameterInput.value || !this.interpForm.controls.flowRateInput.value) {
    this.interpolatedPoint = null;
    this.plotData();
    return;
  }
  // Get all profiles sorted by diameter
  const profiles = this.plotFrictionFactor || this.showFrictionFactorTable ? 
    [...this.frictionalPressureProfilesFriction].sort((a, b) => a.tubularInnerDiameter - b.tubularInnerDiameter) :
    [...this.form.formGroup.controls.frictionalPressureLossProfiles.value].sort((a, b) => a.tubularInnerDiameter - b.tubularInnerDiameter);

  if (profiles.length === 0 || (this.interpForm.controls.innerDiameterInput.value === null || this.interpForm.controls.flowRateInput.value === null)) {
    return null;
  }

  // Find the closest profiles for diameter interpolation
  let lowerProfile = null;
  let upperProfile = null;

  // Case 1: Target diameter is smaller than all profiles
  if (this.interpForm.controls.innerDiameterInput.value <= profiles[0].tubularInnerDiameter) {
    lowerProfile = upperProfile = profiles[0];
  }
  // Case 2: Target diameter is larger than all profiles
  else if (this.interpForm.controls.innerDiameterInput.value >= profiles[profiles.length - 1].tubularInnerDiameter) {
    lowerProfile = upperProfile = profiles[profiles.length - 1];
  }
  // Case 3: Target diameter is between two profiles
  else {
    for (let i = 0; i < profiles.length - 1; i++) {
      if (profiles[i].tubularInnerDiameter <= this.interpForm.controls.innerDiameterInput.value && 
          profiles[i + 1].tubularInnerDiameter >= this.interpForm.controls.innerDiameterInput.value) {
        lowerProfile = profiles[i];
        upperProfile = profiles[i + 1];
        break;
      }
    }
  }

  if (!lowerProfile || !upperProfile) {
    return null;
  }

  // If target diameter matches exactly one profile, just interpolate flow rate on that profile
  if (lowerProfile.tubularInnerDiameter === upperProfile.tubularInnerDiameter) {
    this.interpolatedPoint = this.interpolateOnSingleProfileLogLog(lowerProfile, this.interpForm.controls.flowRateInput.value);
    this.plotData();
    return;
  }

  // For diameter interpolation, we need pressure losses at target flow rate for both profiles
  const lowerPressure = this.interpolateOnSingleProfileLogLog(lowerProfile, this.interpForm.controls.flowRateInput.value);
  const upperPressure = this.interpolateOnSingleProfileLogLog(upperProfile, this.interpForm.controls.flowRateInput.value);

  if (lowerPressure === null || upperPressure === null) {
    return null;
  }

  // Linear interpolation between diameters
  const diameterRatio = (this.interpForm.controls.innerDiameterInput.value - lowerProfile.tubularInnerDiameter) / 
                       (upperProfile.tubularInnerDiameter - lowerProfile.tubularInnerDiameter);
  
  this.interpolatedPoint = lowerPressure + diameterRatio * (upperPressure - lowerPressure);
  this.plotData();
}

private interpolateOnSingleProfileLogLog(profile: any, targetFlowRate: number): number | null {
  const dataPoints = [];
  
  if (profile.flowRateOne !== null && profile.pressureLossOne !== null) {
    dataPoints.push({
      flowRate: profile.flowRateOne,
      pressureLoss: profile.pressureLossOne
    });
  }
  
  if (profile.flowRateTwo !== null && profile.pressureLossTwo !== null) {
    dataPoints.push({
      flowRate: profile.flowRateTwo,
      pressureLoss: profile.pressureLossTwo
    });
  }
  
  if (profile.flowRateThree !== null && profile.pressureLossThree !== null) {
    dataPoints.push({
      flowRate: profile.flowRateThree,
      pressureLoss: profile.pressureLossThree
    });
  }
  
  // Sort by flow rate
  dataPoints.sort((a, b) => a.flowRate - b.flowRate);

  if (dataPoints.length === 0) {
    return null;
  }

  // Exact match check
  const exactMatch = dataPoints.find(p => p.flowRate === targetFlowRate);
  if (exactMatch) {
    return exactMatch.pressureLoss;
  }

  // Check if target is outside flow rate range
  if (targetFlowRate <= dataPoints[0].flowRate) {
    return dataPoints[0].pressureLoss;
  }
  if (targetFlowRate >= dataPoints[dataPoints.length - 1].flowRate) {
    return dataPoints[dataPoints.length - 1].pressureLoss;
  }

  // Find bounding points for interpolation
  let lowerIndex = 0;
  for (let i = 0; i < dataPoints.length - 1; i++) {
    if (dataPoints[i].flowRate < targetFlowRate && dataPoints[i + 1].flowRate > targetFlowRate) {
      lowerIndex = i;
      break;
    }
  }

  const lowerPoint = dataPoints[lowerIndex];
  const upperPoint = dataPoints[lowerIndex + 1];

  // Log-log interpolation (power law fit)
  const logFlowRatio = Math.log(targetFlowRate / lowerPoint.flowRate) / Math.log(upperPoint.flowRate / lowerPoint.flowRate);
  
  return lowerPoint.pressureLoss * Math.pow(upperPoint.pressureLoss / lowerPoint.pressureLoss, logFlowRatio);
}
}
